/**
 * Board component using react-chessboard.
 * Wraps the library with the BoardAdapter interface for future replacement.
 */

import { Chessboard } from 'react-chessboard';
import type { Square, Piece } from 'react-chessboard/dist/chessboard/types';
import './Board.css';

export interface BoardProps {
    /** Current position in FEN format */
    fen: string;
    /** Board orientation */
    orientation: 'white' | 'black';
    /** Callback when a move is made */
    onMove: (from: string, to: string, promotion?: string) => void;
    /** Last move to highlight */
    lastMove?: { from: string; to: string };
    /** Legal moves from each square (for hints) */
    legalMoves?: Map<string, string[]>;
    /** Whether the board is disabled (can't make moves) */
    disabled?: boolean;
}

export function Board({
    fen,
    orientation,
    onMove,
    lastMove,
    legalMoves,
    disabled = false,
}: BoardProps) {
    // Only allow dragging pieces of the player's color
    const isDraggablePiece = ({ piece }: { piece: Piece; sourceSquare: Square }) => {
        if (disabled) return false;
        // Piece format is like 'wP', 'bK' - first char is color
        const pieceColor = piece[0] === 'w' ? 'white' : 'black';
        return pieceColor === orientation;
    };

    // Handle piece drop from react-chessboard
    const onDrop = (sourceSquare: Square, targetSquare: Square, piece: string): boolean => {
        if (disabled) return false;

        // Check if this is a promotion move (pawn to last rank)
        // piece format is like 'wP' or 'bP'
        const isPawn = piece.toLowerCase().endsWith('p');
        const isWhitePromotion = isPawn && piece.startsWith('w') && targetSquare[1] === '8';
        const isBlackPromotion = isPawn && piece.startsWith('b') && targetSquare[1] === '1';

        // For now, auto-promote to queen. Later can add promotion picker.
        const promotion = isWhitePromotion || isBlackPromotion ? 'q' : undefined;

        onMove(sourceSquare, targetSquare, promotion);
        return true; // Let the parent handle validation
    };

    // Custom square styles for highlighting
    const customSquareStyles: Record<string, React.CSSProperties> = {};

    if (lastMove) {
        customSquareStyles[lastMove.from] = {
            backgroundColor: 'var(--board-highlight-from)',
        };
        customSquareStyles[lastMove.to] = {
            backgroundColor: 'var(--board-highlight-to)',
        };
    }

    return (
        <div className={`board-container ${disabled ? 'board-disabled' : ''}`}>
            <Chessboard
                position={fen}
                boardOrientation={orientation}
                onPieceDrop={onDrop}
                isDraggablePiece={isDraggablePiece}
                customSquareStyles={customSquareStyles}
                customBoardStyle={{
                    borderRadius: 'var(--radius-lg)',
                    boxShadow: 'var(--shadow-lg)',
                }}
                customDarkSquareStyle={{ backgroundColor: 'var(--board-dark)' }}
                customLightSquareStyle={{ backgroundColor: 'var(--board-light)' }}
                areArrowsAllowed={false}
                showBoardNotation={false}
                animationDuration={200}
            />
        </div>
    );
}

